import React, { useState } from 'react';
import { useAppSelector } from '../../../../shared/hooks/use-app-selector';
import { useNavigate } from 'react-router-dom';
import { SubscriptionUpgradeCard } from './SubscriptionUpgradeCard';
import { OnboardingProgressCard } from './OnboardingProgressCard';
import {
  isWhitelabelAccount,
  isResellerAccount,
} from '../../../../shared/utils';
import { getAppSettings } from '../../../../selectors';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import { OnboardingProgressModal } from './OnboardingProgressModal';
import {
  useFetchOnboardingSteps,
  useSkipOnboardingStep,
} from '@shared/hooks/onboardings';

type SidebarActionCardsProps = {
  sideMenuCollapsed: boolean;
};

export function SidebarActionCards({
  sideMenuCollapsed,
}: SidebarActionCardsProps) {
  const navigate = useNavigate();
  const [openOnboardingProgressModal, setOpenOnboardingProgressModal] =
    useState(false);
  const { trackMethod } = useGlobals();

  const { data: onboardingStepsData, isLoading: isLoadingOnboardingSteps } =
    useFetchOnboardingSteps();

  const { mutate: mutateSkipOnboardingStep } = useSkipOnboardingStep();

  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const currentAccount = useAppSelector((state) => state.currentAccount);
  const { subscriptionPartner } =
    useAppSelector((state) => getAppSettings(state)) || {};

  const packageCode = useAppSelector(
    (state) => state.currentMerchant.packageCode
  );
  const isWhitelabel = isWhitelabelAccount(currentMerchant.whiteLabel);
  const isKeyAccount = Boolean(currentAccount?.isKeyAccount);
  const isTrialMerchant = (currentMerchant?.trialDaysLeft || 0) > 0;
  const isReseller = isResellerAccount(subscriptionPartner);

  const filteredOnboardingSteps = onboardingStepsData
    ? {
        ...onboardingStepsData,
        steps: onboardingStepsData.steps.filter((step) =>
          isReseller ? step.id !== 'activate_subscription' : true
        ),
      }
    : undefined;

  const showOnboardingProgressCard =
    (filteredOnboardingSteps?.overall_progress_percentage ?? 100) < 100;

  const showSubscriptionUpgradeCard =
    !isWhitelabel &&
    !isReseller &&
    !isKeyAccount &&
    (isTrialMerchant || packageCode !== 'shore_pos_premium') &&
    !showOnboardingProgressCard;

  const handleOnSubscriptionUpgradeCardClick = () => {
    trackMethod('subscription_upgrade_card_clicked', {
      isTrialMerchant,
      trialDaysLeft: currentMerchant?.trialDaysLeft || 0,
    });
    if (isTrialMerchant) {
      navigate('/subscription');
    } else {
      navigate('/subscription/upgrade');
    }
  };

  const handleOnOnboardingProgressCardClick = () => {
    trackMethod('onboarding_progress_card_clicked');
    setOpenOnboardingProgressModal(true);

    if (!openOnboardingProgressModal) {
      trackMethod('onboarding_progress_banner_displayed', {
        platform: 'booking',
        overall_progress_percentage:
          onboardingStepsData?.overall_progress_percentage ?? 0,
        merchant_id: currentMerchant.id,
      });
    }
  };

  const handleOnSkipOnboardingStepClick = () => {
    mutateSkipOnboardingStep();
    setOpenOnboardingProgressModal(false);
  };

  return (
    <>
      {showSubscriptionUpgradeCard && (
        <SubscriptionUpgradeCard
          sideMenuCollapsed={sideMenuCollapsed}
          onCardClick={handleOnSubscriptionUpgradeCardClick}
        />
      )}
      {showOnboardingProgressCard && (
        <OnboardingProgressCard
          sideMenuCollapsed={sideMenuCollapsed}
          onboardingStepsData={filteredOnboardingSteps}
          onCardClick={handleOnOnboardingProgressCardClick}
        />
      )}
      <OnboardingProgressModal
        openModal={openOnboardingProgressModal}
        onboardingStepsData={filteredOnboardingSteps}
        isLoadingOnboardingSteps={isLoadingOnboardingSteps}
        onCloseModalClick={() => setOpenOnboardingProgressModal(false)}
        onSkipStepClick={handleOnSkipOnboardingStepClick}
      />
    </>
  );
}
